import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { FilterCard } from "../components/FilterCard";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { StatusBadge } from "../components/StatusBadge";
import { TablePagination } from "../components/TablePagination";
import { sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { deleteAdminUser } from "../api/adminApi";
import { useAdminUsers } from "../hooks/useAdminUsers";
import { getAuthUser, isSuperAdminUser } from "../auth";
import type { UserAccount } from "../types";

function isAdminAccountRole(role: UserAccount["role"]): boolean {
  return role === "admin" || role === "super_admin";
}

function canManageUser(account: UserAccount): boolean {
  if (isSuperAdminUser()) return true;
  const me = getAuthUser();
  if (me && account.id === String(me.id)) return true;
  return !isAdminAccountRole(account.role);
}

export function UsersPage() {
  const { t } = useTranslation();
  const { exportTable } = usePageActions();
  const { users, loading, error, usingApi, roleOptions, reload } = useAdminUsers();
  const { showToast } = useToast();
  const [roleFilter, setRoleFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [appliedRole, setAppliedRole] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [appliedEmail, setAppliedEmail] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const labels = t.safecheck.usersPage;

  const applyFilters = async () => {
    setAppliedRole(roleFilter);
    setAppliedName(nameFilter.trim());
    setAppliedEmail(emailFilter.trim());
    setPage(1);
    await reload({
      role: roleFilter || undefined,
      name: nameFilter.trim() || undefined,
      email: emailFilter.trim() || undefined,
    });
  };

  const resetFilters = async () => {
    setRoleFilter("");
    setNameFilter("");
    setEmailFilter("");
    setAppliedRole("");
    setAppliedName("");
    setAppliedEmail("");
    setTableSearch("");
    setPage(1);
    await reload({});
  };

  const handleDelete = async (userId: string, userName: string) => {
    if (!usingApi) return;
    if (!window.confirm(`Supprimer l'utilisateur « ${userName} » ?`)) return;

    setDeletingId(userId);
    try {
      await deleteAdminUser(Number(userId));
      showToast("Utilisateur supprimé.");
      await reload({
        role: appliedRole || undefined,
        name: appliedName || undefined,
        email: appliedEmail || undefined,
      });
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Suppression impossible.");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    // Filtre tableSearch reste client ; rôle/nom/email viennent de l'API (applied*).
    return users.filter((account) => {
      const matchTable = tableSearch
        ? `${account.id} ${account.name} ${account.email}`.toLowerCase().includes(tableSearch.toLowerCase())
        : true;
      return matchTable;
    });
  }, [users, tableSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageData = filtered.slice(start, start + pageSize);

  const handleExport = () => {
    exportTable(
      "utilisateurs.csv",
      ["ID", "Nom", "Email", "Rôle"],
      filtered.map((account) => [
        account.id,
        account.name,
        account.email,
        account.role,
      ]),
    );
  };

  const roleVariant: Record<UserAccount["role"], "success" | "danger" | "info" | "purple"> = {
    hospital: "success",
    admin: "danger",
    super_admin: "danger",
    auditor: "info",
    enterprise: "purple",
  };

  const roleLabel = (role: UserAccount["role"]) => {
    if (role === "super_admin") return "Super admin";
    if (role === "admin") return t.safecheck.usersPage.roles.admin;
    if (role === "auditor") return t.safecheck.usersPage.roles.auditor;
    if (role === "enterprise") return t.safecheck.usersPage.roles.enterprise;
    return t.safecheck.usersPage.roles.hospital;
  };

  return (
    <>
      <Breadcrumbs items={[{ label: t.safecheck.nav.users }]} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{labels.title}</h1>
          {usingApi && !loading ? (
            <p className="mt-1 text-sm text-muted">{filtered.length} utilisateur(s)</p>
          ) : null}
          {loading ? <p className="mt-1 text-sm text-muted">Chargement…</p> : null}
          {error ? (
            <p className="mt-1 text-sm text-red-600">{sanitizeApiErrorMessage(error)}</p>
          ) : null}
        </div>
        <Link
          to="/dashboard/users/create"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          {labels.addUser}
        </Link>
      </div>

      <FilterCard
        title={t.safecheck.filters}
        onFilter={() => {
          void applyFilters();
        }}
        onReset={() => {
          void resetFilters();
        }}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.filters.role}>
            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              className={selectClass}
            >
              <option value="">{labels.filters.allRoles}</option>
              {roleOptions
                .filter((option) => option.value)
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </FormField>

          <FormField label={labels.filters.username}>
            <input
              value={nameFilter}
              onChange={(event) => setNameFilter(event.target.value)}
              placeholder={labels.filters.usernamePlaceholder}
              className={inputClass}
            />
          </FormField>

          <FormField label={labels.filters.email}>
            <input
              value={emailFilter}
              onChange={(event) => setEmailFilter(event.target.value)}
              placeholder={labels.filters.emailPlaceholder}
              className={inputClass}
            />
          </FormField>
        </div>
      </FilterCard>

      <div className="theme-card overflow-x-auto">
        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder={labels.table.searchPlaceholder}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
          onExport={handleExport}
          exportLabel={labels.exportCsv}
        />

        <div className="min-w-[720px]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-card-muted/40 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-5 py-3">ID</th>
                <th className="px-5 py-3">Nom</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Rôle</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm text-muted">
                    Aucun utilisateur trouvé.
                  </td>
                </tr>
              ) : (
                pageData.map((account) => (
                  <tr key={account.id} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 text-sm text-foreground">{account.id}</td>
                    <td className="px-5 py-3 text-sm font-medium text-foreground">{account.name}</td>
                    <td className="px-5 py-3 text-sm text-muted">{account.email}</td>
                    <td className="px-5 py-3">
                      <StatusBadge variant={roleVariant[account.role]}>{roleLabel(account.role)}</StatusBadge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {canManageUser(account) ? (
                          <>
                            <Link
                              to={`/dashboard/users/${account.id}/edit`}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
                            >
                              <Pencil size={14} />
                            </Link>
                            <button
                              type="button"
                              disabled={!usingApi || deletingId === account.id}
                              onClick={() => void handleDelete(account.id, account.name)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-danger transition-colors hover:bg-danger-soft disabled:opacity-50"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-muted">Réservé super admin</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={filtered.length}
          onPageChange={(nextPage) => {
            setPage(nextPage);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          labels={t.safecheck.tablePagination}
        />
      </div>
    </>
  );
}
